import React, { useState, useEffect, useCallback } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dashCoverPriceSheetService } from '../../api/services/dashCoverPriceSheetService';
import { toast } from 'react-hot-toast';

export default function DashCoverPriceSheetManagement() {
  const queryClient = useQueryClient();
  const [selectedSheet, setSelectedSheet] = useState<number | null>(null);
  const [newSheetNumber, setNewSheetNumber] = useState<string>('');
  const [selectedFabric, setSelectedFabric] = useState<string>('');
  const [selectedTier, setSelectedTier] = useState<string>('');
  const [selectedPriceCode, setSelectedPriceCode] = useState<string>('');
  const [sheetData, setSheetData] = useState<Record<string, number>>({});
  const [trimFabrics, setTrimFabrics] = useState<Record<string, string>>({});
  const [trimTiers, setTrimTiers] = useState<Record<string, number>>({});
  const [isDirty, setIsDirty] = useState(false);

  // Add row state
  const [newTrimCode, setNewTrimCode] = useState<string>('');
  const [newFabricName, setNewFabricName] = useState<string>('');
  const [newTier, setNewTier] = useState<string>('');

  // Mass action state
  const [massActionType, setMassActionType] = useState<string>('set');
  const [massActionValue, setMassActionValue] = useState<string>('');
  const [massActionPriceCode, setMassActionPriceCode] = useState<string>('');

  // Fetch metadata (sheet names, trim codes, price codes)
  const { data: metadata, isLoading: metadataLoading } = useQuery({
    queryKey: ['dashCoverPriceSheetMetadata'],
    queryFn: () => dashCoverPriceSheetService.getMetadata(),
  });

  // Fetch all sheet data when a sheet is selected (filter client-side to preserve edits)
  const { data: fetchedSheetData, isLoading: sheetLoading } = useQuery({
    queryKey: ['dashCoverPriceSheetData', selectedSheet],
    queryFn: () => dashCoverPriceSheetService.getSheetData(selectedSheet!),
    enabled: selectedSheet !== null,
  });

  // Update local state when fetched data changes
  useEffect(() => {
    if (fetchedSheetData) {
      setSheetData(fetchedSheetData.data);
      setTrimFabrics(fetchedSheetData.trimFabrics);
      setTrimTiers(fetchedSheetData.trimTiers);
      setIsDirty(false);
    }
  }, [fetchedSheetData]);

  // Clear data when switching to "New"
  useEffect(() => {
    if (selectedSheet === null) {
      setSheetData({});
      setTrimFabrics({});
      setTrimTiers({});
      setIsDirty(false);
    }
  }, [selectedSheet]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: () => {
      const sheetNumber = selectedSheet !== null ? selectedSheet : parseInt(newSheetNumber, 10);
      return dashCoverPriceSheetService.saveSheetData(sheetNumber, sheetData, trimFabrics, trimTiers);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashCoverPriceSheetMetadata'] });
      queryClient.invalidateQueries({ queryKey: ['dashCoverPriceSheetData'] });
      toast.success('Price sheet saved successfully');
      setIsDirty(false);
      if (selectedSheet === null && newSheetNumber) {
        setSelectedSheet(parseInt(newSheetNumber, 10));
        setNewSheetNumber('');
      }
    },
  });

  const getKey = useCallback((trimCode: string, priceCode: string) => {
    return `${trimCode}-${priceCode}`;
  }, []);

  const handlePriceChange = (trimCode: string, priceCode: string, value: string) => {
    const key = getKey(trimCode, priceCode);
    setSheetData(prev => ({
      ...prev,
      [key]: value === '' ? 0 : parseFloat(value),
    }));
    setIsDirty(true);
  };

  const handleSheetChange = (value: string) => {
    if (value === '') {
      setSelectedSheet(null);
    } else {
      setSelectedSheet(parseInt(value, 10));
    }
  };

  const handleSave = () => {
    if (selectedSheet === null) {
      const num = parseInt(newSheetNumber, 10);
      if (isNaN(num) || num < 1000 || num > 9999) {
        toast.error('New sheet number must be between 1000 and 9999');
        return;
      }
    }
    saveMutation.mutate();
  };

  const handleMassAction = () => {
    const value = parseFloat(massActionValue);
    if (isNaN(value)) {
      toast.error('Please enter a valid number');
      return;
    }

    if (!massActionPriceCode) {
      toast.error('Please select a price code for the mass action');
      return;
    }

    setSheetData(prev => {
      const updated = { ...prev };
      for (const trim of trims) {
        const key = getKey(trim, massActionPriceCode);
        const current = updated[key] || 0;

        switch (massActionType) {
          case 'set':
            updated[key] = value;
            break;
          case 'increase_amount':
            updated[key] = Math.round((current + value) * 100) / 100;
            break;
          case 'decrease_amount':
            updated[key] = Math.max(0, Math.round((current - value) * 100) / 100);
            break;
          case 'increase_percent':
            updated[key] = Math.round(current * (1 + value / 100) * 100) / 100;
            break;
          case 'decrease_percent':
            updated[key] = Math.max(0, Math.round(current * (1 - value / 100) * 100) / 100);
            break;
        }
      }
      return updated;
    });
    setIsDirty(true);
    toast.success(`Mass action applied to price code "${massActionPriceCode}" for ${trims.length} rows`);
  };

  const handleAddRow = () => {
    const trimCode = newTrimCode.trim();
    if (!trimCode) {
      toast.error('Please enter a trim code');
      return;
    }

    // Check if trim already exists
    const existingKeys = Object.keys(sheetData);
    const alreadyExists = existingKeys.some(key => key.slice(0, -2) === trimCode);
    if (alreadyExists) {
      toast.error(`Trim code "${trimCode}" already exists in this sheet`);
      return;
    }

    // Add empty entries for all price codes
    setSheetData(prev => {
      const updated = { ...prev };
      for (const pc of (metadata?.priceCodes || [])) {
        updated[`${trimCode}-${pc}`] = 0;
      }
      return updated;
    });

    if (newFabricName.trim()) {
      setTrimFabrics(prev => ({ ...prev, [trimCode]: newFabricName.trim() }));
    }

    if (newTier) {
      setTrimTiers(prev => ({ ...prev, [trimCode]: parseInt(newTier, 10) }));
    }

    setIsDirty(true);
    toast.success(`Added trim code "${trimCode}"`);
    setNewTrimCode('');
    setNewFabricName('');
    setNewTier('');
  };

  const handleDeleteRow = (trimCode: string) => {
    setSheetData(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updated)) {
        if (key.slice(0, -2) === trimCode) {
          delete updated[key];
        }
      }
      return updated;
    });
    setTrimFabrics(prev => {
      const updated = { ...prev };
      delete updated[trimCode];
      return updated;
    });
    setTrimTiers(prev => {
      const updated = { ...prev };
      delete updated[trimCode];
      return updated;
    });
    setIsDirty(true);
    toast.success(`Removed trim code "${trimCode}"`);
  };

  const isNewSheet = selectedSheet === null;
  const allPriceCodes = metadata?.priceCodes || [];

  // Filter visible price codes
  const visiblePriceCodes = selectedPriceCode
    ? allPriceCodes.filter(pc => pc === selectedPriceCode)
    : allPriceCodes;

  // Derive all trims from sheet data
  const allTrims = React.useMemo(() => {
    const trimSet = new Set<string>();
    for (const key of Object.keys(sheetData)) {
      const trimCode = key.slice(0, -2);
      if (trimCode) trimSet.add(trimCode);
    }
    return Array.from(trimSet).sort((a, b) => {
      const fabricA = (trimFabrics[a] || '').toLowerCase();
      const fabricB = (trimFabrics[b] || '').toLowerCase();
      if (fabricA !== fabricB) return fabricA.localeCompare(fabricB);
      return a.localeCompare(b);
    });
  }, [sheetData, trimFabrics]);

  // Apply fabric/tier filters client-side
  const trims = React.useMemo(() => {
    return allTrims.filter(trim => {
      if (selectedFabric && (trimFabrics[trim] || '') !== selectedFabric) return false;
      if (selectedTier && (trimTiers[trim] ?? '') !== parseInt(selectedTier, 10)) return false;
      return true;
    });
  }, [allTrims, selectedFabric, selectedTier, trimFabrics, trimTiers]);

  return (
    <MainLayout>
      <div className="px-4 py-6 sm:px-0">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Price Sheet Manager — Dash Covers
            </h2>
            <div className="flex items-center gap-3">
              {isDirty && (
                <span className="text-sm text-amber-600 dark:text-amber-400">
                  Unsaved changes
                </span>
              )}
              <button
                onClick={handleSave}
                disabled={saveMutation.isPending || (selectedSheet === null && !newSheetNumber)}
                className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {saveMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          <div className="p-6">
            {metadataLoading ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Loading...
              </div>
            ) : (
              <>
                {/* Filters */}
                <div className="mb-6 flex flex-wrap items-end gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Select Price Sheet
                    </label>
                    <select
                      value={selectedSheet !== null ? selectedSheet.toString() : ''}
                      onChange={(e) => handleSheetChange(e.target.value)}
                      className="block w-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="">New</option>
                      {metadata?.sheets.map((sheet) => (
                        <option key={sheet} value={sheet.toString()}>
                          {sheet}
                        </option>
                      ))}
                    </select>
                  </div>

                  {isNewSheet && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        New Sheet Number
                      </label>
                      <input
                        type="number"
                        min="1000"
                        max="9999"
                        value={newSheetNumber}
                        onChange={(e) => setNewSheetNumber(e.target.value)}
                        placeholder="e.g. 1000"
                        className="block w-40 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Fabric
                    </label>
                    <select
                      value={selectedFabric}
                      onChange={(e) => setSelectedFabric(e.target.value)}
                      className="block w-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="">All Fabrics</option>
                      {metadata?.fabricNames.map((fabric) => (
                        <option key={fabric} value={fabric}>
                          {fabric}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tier
                    </label>
                    <select
                      value={selectedTier}
                      onChange={(e) => setSelectedTier(e.target.value)}
                      className="block w-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="">All Tiers</option>
                      {metadata?.tiers.map((tier) => (
                        <option key={tier} value={tier.toString()}>
                          {tier}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Price Code
                    </label>
                    <select
                      value={selectedPriceCode}
                      onChange={(e) => setSelectedPriceCode(e.target.value)}
                      className="block w-36 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                    >
                      <option value="">All Codes</option>
                      {allPriceCodes.map((pc) => (
                        <option key={pc} value={pc}>
                          {pc}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Mass Action */}
                {trims.length > 0 && (
                  <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                      Mass Action
                    </h3>
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Price Code
                        </label>
                        <select
                          value={massActionPriceCode}
                          onChange={(e) => setMassActionPriceCode(e.target.value)}
                          className="block w-28 px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                        >
                          <option value="">Select</option>
                          {allPriceCodes.map((pc) => (
                            <option key={pc} value={pc}>
                              {pc}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Action
                        </label>
                        <select
                          value={massActionType}
                          onChange={(e) => setMassActionType(e.target.value)}
                          className="block w-44 px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                        >
                          <option value="set">Set to value</option>
                          <option value="increase_amount">Increase by $</option>
                          <option value="decrease_amount">Decrease by $</option>
                          <option value="increase_percent">Increase by %</option>
                          <option value="decrease_percent">Decrease by %</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Value
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={massActionValue}
                          onChange={(e) => setMassActionValue(e.target.value)}
                          placeholder="0.00"
                          className="block w-28 px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                        />
                      </div>

                      <button
                        onClick={handleMassAction}
                        disabled={!massActionPriceCode || !massActionValue}
                        className="px-4 py-2 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        Apply to {trims.length} rows
                      </button>
                    </div>
                  </div>
                )}

                {/* Add Row */}
                {(selectedSheet !== null || newSheetNumber) && (
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                      Add Trim Code
                    </h3>
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Trim Code *
                        </label>
                        <input
                          type="text"
                          value={newTrimCode}
                          onChange={(e) => setNewTrimCode(e.target.value)}
                          placeholder="e.g. AB"
                          className="block w-28 px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Fabric Name
                        </label>
                        <input
                          type="text"
                          value={newFabricName}
                          onChange={(e) => setNewFabricName(e.target.value)}
                          placeholder="e.g. Leather"
                          className="block w-40 px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Tier
                        </label>
                        <select
                          value={newTier}
                          onChange={(e) => setNewTier(e.target.value)}
                          className="block w-24 px-2 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                        >
                          <option value="">None</option>
                          {metadata?.tiers.map((t) => (
                            <option key={t} value={t.toString()}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={handleAddRow}
                        disabled={!newTrimCode.trim()}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        Add Row
                      </button>
                    </div>
                  </div>
                )}

                {/* Price Table */}
                {sheetLoading ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Loading sheet data...
                  </div>
                ) : trims.length > 0 && visiblePriceCodes.length > 0 ? (
                  <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-10">
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-700 z-10">
                            Fabric
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Trim Code
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Tier
                          </th>
                          {visiblePriceCodes.map((pc) => (
                            <th
                              key={pc}
                              className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                              {pc}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {trims.map((trim, idx) => (
                          <tr
                            key={trim}
                            className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-900'}
                          >
                            <td className="px-2 py-1 text-center">
                              <button
                                onClick={() => handleDeleteRow(trim)}
                                className="text-red-400 hover:text-red-600 dark:hover:text-red-300 text-sm"
                                title={`Remove ${trim}`}
                              >
                                &times;
                              </button>
                            </td>
                            <td className="px-2 py-1 whitespace-nowrap sticky left-0 bg-inherit z-10">
                              <input
                                type="text"
                                value={trimFabrics[trim] || ''}
                                onChange={(e) => {
                                  setTrimFabrics(prev => ({ ...prev, [trim]: e.target.value }));
                                  setIsDirty(true);
                                }}
                                placeholder="—"
                                className="block w-32 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                              />
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                              {trim}
                            </td>
                            <td className="px-2 py-1 text-center">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={trimTiers[trim] !== undefined && trimTiers[trim] !== null ? trimTiers[trim] : ''}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setTrimTiers(prev => {
                                    const updated = { ...prev };
                                    if (val === '') {
                                      delete updated[trim];
                                    } else {
                                      updated[trim] = parseInt(val, 10);
                                    }
                                    return updated;
                                  });
                                  setIsDirty(true);
                                }}
                                placeholder="—"
                                className="block w-16 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                              />
                            </td>
                            {visiblePriceCodes.map((pc) => {
                              const key = getKey(trim, pc);
                              const value = sheetData[key];
                              return (
                                <td key={pc} className="px-2 py-1">
                                  <input
                                    type="number"
                                    min="0"
                                    max="999"
                                    step="0.01"
                                    value={value !== undefined && value !== null ? value : ''}
                                    onChange={(e) => handlePriceChange(trim, pc, e.target.value)}
                                    className="block w-20 px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-gray-100"
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No trim codes or price codes found in the database.
                  </div>
                )}

              </>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
